/**
 * Rozvrh celého kalendáře: z načteného okna měsíců postaví buď **souvislý kalendář po týdnech**
 * (svislý posuv), nebo **jednu souvislou osu dnů** (vodorovný posuv).
 *
 * Souvislý kalendář neřeže měsíce do samostatných mřížek — týdny plynou bez přerušení a měsíční
 * pruh se vloží před první týden, jehož **pondělí** do daného měsíce patří. Řádek se tedy nikdy
 * nedělí napůl a nevznikají slepé dny sousedního měsíce.
 *
 * Rozvrh se počítá **přírůstkově** (viz {@link CalendarLayoutCache}). Sám o sobě je levný — celé
 * okno tří let vyjde na jednotky milisekund — ale jeho výsledek se vykresluje do stovek řádků,
 * a to už levné není. Když tedy okno doroste o pár měsíců, řádky, kterých se to netýká, si podrží
 * svou identitu a React je přeskočí.
 */
import { mondayOfDay, monthIndex, type MonthAnchor } from './days';
import { windowRange, type CalendarWindow } from './calendarWindow';
import { layoutRow, type CalendarRowModel, type RowLayoutInput } from './rowLayout';
import type { CalendarTermView } from './terms';

/** Blok měsíce v souvislém kalendáři — sticky popisek a jeho týdny. */
export interface CalendarMonthGroup {
  key: string;
  label: string;
  /** `false` u souvislé osy — popisky měsíců tam nese pruh nad dny. */
  showLabel: boolean;
  rows: CalendarRowModel[];
}

export type CalendarAxis = 'weeks' | 'timeline';

export interface CalendarLayoutInput {
  /** Načtené okno měsíců — roste, jak uživatel doscrolluje k jeho hraně. */
  window: CalendarWindow;
  axis: CalendarAxis;
  terms: readonly CalendarTermView[];
  /** Šířka dne v px — u týdnů změřená z mřížky, u osy pevná (zoom). */
  dayWidth: number;
  today: number;
  realTime: boolean;
  monthNames: readonly string[];
  formatNights: (nights: number) => string;
}

/**
 * Cache řádků mezi voláními. Řádek se přepočítá jen tehdy, když se změnilo něco, co ho opravdu
 * ovlivňuje — tedy jeho vlastní dny, nebo termíny, které do nich zasahují.
 */
export interface CalendarLayoutCache {
  rows: Map<number, { signature: string; row: CalendarRowModel }>;
  /** Průběžně přidělovaná id objektů, aby se daly porovnávat v textovém otisku. */
  ids: WeakMap<object, number>;
  nextId: number;
}

export function createCalendarLayoutCache(): CalendarLayoutCache {
  return { rows: new Map(), ids: new WeakMap(), nextId: 1 };
}

function idOf(cache: CalendarLayoutCache, value: object): number {
  let id = cache.ids.get(value);
  if (id === undefined) {
    id = cache.nextId++;
    cache.ids.set(value, id);
  }
  return id;
}

/**
 * Otisk řádku: společný základ (co platí pro celé okno) plus termíny, které do řádku zasahují.
 * Termín se do otisku podepisuje **identitou svého eventu** — ta se mění jen s odpovědí serveru,
 * takže změna dat řádek správně zneplatní, zatímco pouhé přepočítání pohledu ne.
 */
function rowSignature(
  base: string,
  from: number,
  cols: number,
  terms: readonly CalendarTermView[],
  cache: CalendarLayoutCache
): string {
  const to = from + cols - 1;
  let signature = `${base}|${cols}`;
  for (const term of terms) {
    if (term.to < from || term.from > to) continue;
    signature += `|${idOf(cache, term.event)}:${term.from}:${term.to}:${term.label}`;
  }
  return signature;
}

export function buildCalendarLayout(
  input: CalendarLayoutInput,
  cache?: CalendarLayoutCache
): CalendarMonthGroup[] {
  const shared: Omit<RowLayoutInput, 'from' | 'cols'> = {
    terms: input.terms,
    dayWidth: input.dayWidth,
    today: input.today,
    realTime: input.realTime,
    monthNames: input.monthNames,
    formatNights: input.formatNights,
  };

  // Základ otisku — co je společné všem řádkům okna. `formatNights` se podepíše svým výstupem,
  // protože se mění s jazykem UI.
  const base = [
    input.dayWidth,
    input.today,
    input.realTime,
    input.monthNames[0],
    input.formatNights(1),
  ].join('|');
  const used = new Set<number>();

  const row = (from: number, cols: number): CalendarRowModel => {
    if (!cache) return layoutRow({ ...shared, from, cols });
    used.add(from);
    const signature = rowSignature(base, from, cols, input.terms, cache);
    const cached = cache.rows.get(from);
    if (cached && cached.signature === signature) return cached.row;
    const built = layoutRow({ ...shared, from, cols });
    cache.rows.set(from, { signature, row: built });
    return built;
  };

  /** Řádky, které v okně nezůstaly, z cache vypadnou — jinak by rostla donekonečna. */
  const prune = () => {
    if (!cache) return;
    for (const from of [...cache.rows.keys()]) {
      if (!used.has(from)) cache.rows.delete(from);
    }
  };

  if (input.axis === 'timeline') {
    const { start, end } = windowRange(input.window);
    const timeline = row(start, end - start + 1);
    prune();
    return [{ key: 'timeline', label: '', showLabel: false, rows: [timeline] }];
  }

  const groups: CalendarMonthGroup[] = [];
  const lastIndex = monthIndex(input.window.last);
  let monday = mondayOfDay(windowRange(input.window).start);
  let group: CalendarMonthGroup | null = null;

  for (;;) {
    const date = new Date(monday * 86_400_000);
    const current: MonthAnchor = { year: date.getUTCFullYear(), month: date.getUTCMonth() };
    if (monthIndex(current) > lastIndex) break;

    const week = row(monday, 7);
    const key = String(monthIndex(current));
    if (!group || group.key !== key) {
      group = {
        key,
        label: `${input.monthNames[current.month] ?? ''} ${current.year}`,
        showLabel: true,
        rows: [],
      };
      groups.push(group);
    }
    group.rows.push(week);
    monday += 7;
  }

  prune();
  return groups;
}
